import React from 'react';
import { useShowInternals } from '../contexts/InternalsContext';

/**
 * Compact read-out of how full the chat's context window is, e.g.
 * `ctx ~15,000/65,000 (23%)`. Replaces the old manual "Compact" button —
 * the backend now auto-compacts older turns once usage crosses its
 * headroom threshold, so this is purely informational.
 *
 * When `contextWindow` is unknown (null/0 — the model isn't in our
 * model_specs table) we can't compute a fraction, so we show just the
 * estimated token count and no bar.
 *
 * ADMINS ONLY (and only with internals revealed). A token budget is a fact
 * about the machinery, not about the work: the backend auto-compacts, so there
 * is nothing here for a customer to act on even if they understood it.
 */
export default function ContextUsageChip({
  contextTokens,
  contextWindow,
  className = '',
}: {
  contextTokens: number;
  contextWindow?: number | null;
  className?: string;
}) {
  const showInternals = useShowInternals();
  const tokens = Math.max(0, Math.round(contextTokens || 0));
  const hasWindow = !!contextWindow && contextWindow > 0;
  const pct = hasWindow
    ? Math.min(100, Math.round((tokens / (contextWindow as number)) * 100))
    : null;

  // Tint toward amber/red as the window fills — a glanceable health cue.
  let tone = 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700';
  if (pct !== null) {
    if (pct >= 90) tone = 'text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30';
    else if (pct >= 70) tone = 'text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30';
  }

  const label = hasWindow
    ? `ctx ~${tokens.toLocaleString()}/${(contextWindow as number).toLocaleString()} (${pct}%)`
    : `ctx ~${tokens.toLocaleString()}`;

  if (!showInternals) return null;

  return (
    <span
      title={
        hasWindow
          ? `Estimated context usage: ~${tokens.toLocaleString()} of ${(contextWindow as number).toLocaleString()} tokens. Older turns auto-compact into a summary as this fills.`
          : `Estimated context usage: ~${tokens.toLocaleString()} tokens. Set a context window on this model's LLM settings to enable auto-compaction.`
      }
      className={`px-2 py-0.5 text-[11px] font-mono rounded border ${tone} ${className}`}
    >
      {label}
    </span>
  );
}
